import {
  BASIC_AUTH_HEADER,
  SERVER_AUTH_TOKEN_OVERRIDE_HEADER,
} from "@/types/auth"
import type { SubscribableObjectEvent } from "@/types/socket"
import type { VoiceFeedbackTraceExport } from "@/types/voice-chat"
import uniq from "lodash/uniq"
import { v4 as uuidv4 } from "uuid"

import { nowMillis } from "@/lib/date-utils"
import {
  idbAppend,
  idbClearStore,
  idbCount,
  idbGetChatIndexBoundaryEntries,
  idbGetNewestIndexChunk,
  idbTrimOldest,
  VOICE_TRACE_CHAT_INDEX,
  VOICE_TRACE_LEGACY_SESSION_INDEX,
  VOICE_TRACE_SESSION_INDEX,
  VOICE_TRACE_STORE,
} from "@/lib/ddb"
import { isDevelopment } from "@/lib/utils/misc"

function tuple<const T extends readonly string[]>(values: T) {
  return values
}

export const VOICE_TRACE_SOURCES = tuple([
  "transport",
  "pipeline",
  "persist",
  "tools",
  "gate",
  "captions",
  "settings",
  "http",
  "socket",
  "layout",
])

export type VoiceTraceSource = (typeof VOICE_TRACE_SOURCES)[number]

export const VOICE_TRACE_EVENT_NAMES = tuple([
  "assistant_caption_rate_measured",
  "assistant_interrupt_manual_stop",
  "assistant_interrupt_server_truncated",
  "assistant_stop_requested",
  "assistant_stopped_for_pause",
  "audio_devices_devicechange_refresh_started",
  "audio_devices_enumeration_failed",
  "audio_devices_enumeration_started",
  "audio_devices_enumeration_succeeded",
  "audio_output_failed",
  "audio_output_skipped",
  "audio_output_succeeded",
  "audio_playback_failed",
  "audio_playback_started",
  "caption_gate_window_continued",
  "caption_pacer_tick",
  "caption_reclaim_blocked",
  "caption_scroll_clamp_absorbed",
  "caption_scroll_issued",
  "caption_scroll_observed",
  "caption_scroll_reengage_skipped",
  "caption_scroll_reengaged",
  "caption_scroll_takeover",
  "caption_space_reclaimed",
  "caption_speech_gate",
  "caption_speech_gate_stats",
  "caption_sync_landing",
  "caption_turn_evicted",
  "chat_list_cache_patch_missed_active_voice",
  "chat_list_cache_patched_active_voice",
  "chat_create_failed",
  "chat_create_started",
  "chat_create_succeeded",
  "client_environment",
  "client_event_send_failed",
  "client_event_send_skipped",
  "conversation.item.added",
  "conversation.item.create",
  "conversation.item.created",
  "conversation.item.delete",
  "conversation.item.deleted",
  "conversation.item.retrieve",
  "conversation.item.retrieved",
  "conversation.item.input_audio_transcription.completed",
  "conversation.item.input_audio_transcription.delta",
  "conversation.item.input_audio_transcription.failed",
  "conversation.item.truncated",
  "data_channel_closed",
  "data_channel_error",
  "data_channel_opened",
  "error",
  "finalized_persist",
  "get_user_media_failed",
  "get_user_media_started",
  "get_user_media_succeeded",
  "history_ack_failed",
  "user_speech_before_history_ready",
  "history_item_exceeds_sctp_limit",
  "history_ready",
  "history_send_failed",
  "history_transport_prepared",
  "history_transport_limit_unavailable",
  "greeting_persist_skipped",
  "greeting_response_tagged",
  "input_audio_buffer.speech_started",
  "input_audio_buffer.speech_stopped",
  "location_update_lookup_failed",
  "location_update_sent",
  "media_audio_constraints",
  "media_input_selected",
  "media_track_ended",
  "media_track_muted",
  "media_track_unmuted",
  "mic_disabled_for_closing_response",
  "mic_restored_after_closing_error",
  "mic_restored_after_initial_response",
  "mic_gated_for_start_sound",
  "mute_toggled",
  "object_subscription_authorized",
  "object_subscription_emit_matched",
  "object_subscription_emit_no_match",
  "object_subscription_failed",
  "object_subscription_queued_disconnected",
  "object_subscription_requested",
  "object_subscription_unsubscribed",
  "output_audio_buffer.clear",
  "output_audio_buffer.cleared",
  "output_audio_buffer.started",
  "output_audio_buffer.stopped",
  "output_device_selected",
  "pause_abandoned_for_speech",
  "pause_continuation_attached",
  "pause_continuation_item_merged",
  "pause_cut_extended",
  "pause_resume_abandoned",
  "pause_resume_sent",
  "pause_resume_skipped_stale_narration",
  "pause_toggled",
  "peer_connection_state_changed",
  "peer_ice_connection_state_changed",
  "peer_ice_gathering_state_changed",
  "phantom_interrupt_resume_sent",
  "phantom_interrupt_resume_skipped",
  "provider_error_benign",
  "realtime_event_applied",
  "realtime_event_dropped",
  "remote_description_applied",
  "remote_audio_track_received",
  "response.cancel",
  "response.create",
  "response.created",
  "response.done",
  "response.function_call_arguments.done",
  "response.output_audio_transcript.delta",
  "response.output_audio_transcript.done",
  "response.output_item.added",
  "response_cancel_not_active_ignored",
  "selected_chat_load_failed",
  "session.update",
  "settings_changed",
  "sdp_offer_created",
  "socket_auth_expired",
  "socket_connect_error",
  "socket_connected",
  "socket_disconnected",
  "socket_voice_chat_object_received",
  "tool_result_response_acknowledged",
  "tool_result_response_flushed",
  "tool_result_response_queued",
  "tool_result_response_rejected_active_response",
  "tool_result_response_requested",
  "tool_result_response_sent",
  "transcript_persist_attempt_failed",
  "transcript_persist_enqueued",
  "transcript_persist_failed",
  "transcript_persist_queue_reset",
  "transcript_persist_queue_started",
  "transcript_persist_retry_enqueued",
  "transcript_persist_succeeded",
  "truncated_persist",
  "user_item_empty_transcript_skipped",
  "user_item_transcript_persist_enqueued",
  "voice_chat_subscription_requested",
  "voice_graphql_batch_dispatched",
  "voice_output_speed_selected",
  "voice_output_speed_update_sent",
  "voice_session_connect_failed",
  "voice_session_reconnect_attempt",
  "voice_session_reconnect_exhausted",
  "voice_session_reconnect_failed",
  "voice_session_reconnect_started",
  "voice_session_reconnected",
  "voice_session_start_failed",
  "voice_session_start_requested",
  "voice_session_start_retry",
  "voice_session_stop_requested",
  "voice_session_turn_authority",
  "voice_timeline_layout_shift",
  "voice_tool_arguments_parse_failed",
  "voice_tool_backend_error",
  "voice_tool_backend_terminal_error",
  "voice_tool_backend_terminal_success",
  "voice_tool_dto_resync_started",
  "voice_tool_final_payload_sent",
  "voice_tool_media_sent",
  "voice_tool_pending_resync_started",
  "voice_tool_persistence_wait_failed",
  "voice_tool_persistence_wait_started",
  "voice_tool_persistence_wait_succeeded",
  "voice_tool_result_fetch_completed",
  "voice_tool_result_fetch_failed",
  "voice_tool_result_fetch_started",
  "voice_tool_run_returned",
  "voice_tool_run_started",
  "voice_tool_snapshot_rejected",
  "voice_tool_socket_wake_missing_pending",
  "voice_tool_socket_wake_received",
  "voice_tool_user_item_wait_failed",
  "voice_tool_user_item_wait_rejected",
  "voice_tool_user_item_wait_resolved",
  "voice_tool_user_item_wait_started",
  "voice_tool_wait_started",
  "voice_place_annotation_started",
  "voice_place_annotation_response_created",
  "voice_place_annotation_arguments_done",
  "voice_place_annotation_failed",
  "voice_place_annotation_cancelled",
  "voice_place_annotation_duplicate_skipped",
  "voice_light_trace_upload_failed",
])

export type VoiceTraceEventName = (typeof VOICE_TRACE_EVENT_NAMES)[number]

export interface VoiceTracer {
  info(
    summary: VoiceTraceEventName,
    detail?: Record<string, unknown>,
    direction?: "in" | "out"
  ): void

  warn(
    summary: VoiceTraceEventName,
    detail?: Record<string, unknown>,
    direction?: "in" | "out"
  ): void

  error(
    summary: VoiceTraceEventName,
    detail?: Record<string, unknown>,
    direction?: "in" | "out"
  ): void

  bind(extra: Record<string, unknown>): VoiceTracer
}

export interface VoiceTraceEntry {
  id: string
  ts: number
  chatId?: number | null
  voiceSessionId?: string
  openaiCallId?: string | null
  eventSeq?: number
  drift?: boolean
  category: VoiceTraceSource
  level: "info" | "warn" | "error"
  direction?: "in" | "out"
  summary: string
  detail?: unknown
}

export interface VoiceTraceContext {
  chatId: number | null
  voiceSessionId: string
  openaiCallId?: string | null
}

type VoiceTraceContextInput = Omit<VoiceTraceContext, "voiceSessionId"> & {
  voiceSessionId?: string
}

export type VoiceTraceSessionScope =
  | { kind: "voice"; id: string }
  | { kind: "legacy"; id: string }
export type VoiceTraceScope = VoiceTraceSessionScope | { kind: "all" }

export interface RetainedVoiceTraceSession {
  scope: VoiceTraceSessionScope
  startedAt: number
  latestAt: number
}

interface VoiceTraceExportChunk {
  entries: VoiceTraceEntry[]
  continuationKey?: number
}

export interface VoiceLightTraceExport {
  file: File
  contentEncoding: "gzip" | "identity"
  entryCount: number
  droppedCount: number
}

// Caps the persisted IndexedDB store; flushes trim oldest-first past this.
export const MAX_VOICE_TRACE_ENTRIES = 50_000
export const VOICE_TRACE_EXPORT_CHUNK_SIZE = 1_000

const REDACTED = "REDACTED"
// Auth material, plus the two headers client-axios attaches that identify the
// person rather than the request: precise coordinates and the Amplitude session
// id, which re-links an otherwise pseudonymous trace to their analytics
// profile. Both ride every traced request, so neither says anything a report is
// diagnosed from, and a rated call now uploads its trace off the device.
const REDACTED_HEADERS = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "user-coordinates",
  "x-amplitude-session-id",
  BASIC_AUTH_HEADER.toLowerCase(),
  SERVER_AUTH_TOKEN_OVERRIDE_HEADER.toLowerCase(),
])
const REDACTED_BODY_FIELDS = new Set([
  "token",
  "auth_token",
  "refresh_token",
  "value",
])

const LIGHT_TRACE_EXCLUDED_SOURCES: ReadonlySet<VoiceTraceSource> = new Set([
  "captions",
  "layout",
])
const LIGHT_TRACE_SUMMARIES: ReadonlySet<string> = new Set([
  "tool_result_response_queued",
  "tool_result_response_flushed",
  "tool_result_response_acknowledged",
  "tool_result_response_rejected_active_response",
  "response_cancel_not_active_ignored",
  "phantom_interrupt_resume_sent",
  "phantom_interrupt_resume_skipped",
  "pause_resume_sent",
  "pause_resume_abandoned",
  "response.create",
  "response.created",
  "response.done",
  "error",
  "conversation.item.truncated",
  "output_audio_buffer.started",
  "output_audio_buffer.stopped",
  "output_audio_buffer.cleared",
  "input_audio_buffer.speech_started",
  "input_audio_buffer.speech_stopped",
  "response.function_call_arguments.done",
  "realtime_event_dropped",
  "voice_light_trace_upload_failed",
  "client_environment",
  "voice_session_turn_authority",
  "get_user_media_started",
  "get_user_media_succeeded",
  "get_user_media_failed",
  "data_channel_opened",
  "data_channel_closed",
  "data_channel_error",
  "history_ready",
  "history_ack_failed",
  "history_send_failed",
  "user_speech_before_history_ready",
  "sdp_offer_created",
  "remote_description_applied",
  "remote_audio_track_received",
  "media_track_ended",
  "media_track_muted",
  "media_track_unmuted",
  "socket_disconnected",
  "socket_connect_error",
  "socket_auth_expired",
  "voice_graphql_batch_dispatched",
  "transcript_persist_failed",
  "transcript_persist_attempt_failed",
  "finalized_persist",
  "truncated_persist",
  "user_item_empty_transcript_skipped",
  "transcript_persist_queue_reset",
  "assistant_stop_requested",
  "assistant_interrupt_manual_stop",
  "assistant_interrupt_server_truncated",
  "assistant_stopped_for_pause",
  "pause_abandoned_for_speech",
  "caption_speech_gate_stats",
  "audio_output_failed",
  "audio_playback_failed",
  "mute_toggled",
  "pause_toggled",
  "output_device_selected",
  "voice_output_speed_selected",
])
const LIGHT_TRACE_SUMMARY_PREFIXES = [
  "voice_session_",
  "voice_tool_",
  "voice_place_annotation_",
] as const
const LIGHT_TRACE_DETAIL_KEYS: ReadonlySet<string> = new Set([
  "responseId",
  "itemId",
  "callId",
  "openaiCallId",
  "eventId",
  "clientEventId",
  "serverEventId",
  "activeResponseId",
  "inFlightEventId",
  "trigger",
  "handle",
  "toolName",
  "turnDetectionType",
  "noiseReduction",
  "userAgent",
  "platform",
  "brands",
  "toolResultItemIds",
  "retryCallIds",
  "reason",
  "code",
  "status",
  "attempt",
  "type",
  "readyState",
])

let activeTraceContext: VoiceTraceContext | null = null
let traceEntrySequence = 0
let activeTraceEventSeq: number | null = null
// Write-through recorder: every entry lands here only long enough for the
// self-coalescing flush to drain it to IndexedDB. Voice chat is unstable
// enough that traces always persist as they're recorded — nothing is held
// back in memory, so the trace survives reloads and crashes.
let pendingTraceEntries: VoiceTraceEntry[] = []
let flushQueue: Promise<void> = Promise.resolve()

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function hasToJson(
  value: Record<string, unknown>
): value is Record<string, unknown> & { toJSON: () => unknown } {
  return typeof value.toJSON === "function"
}

export function startVoiceTraceContext(context: VoiceTraceContextInput) {
  activeTraceContext = {
    ...context,
    voiceSessionId: context.voiceSessionId ?? uuidv4(),
  }
}

export function updateVoiceTraceContext(
  context: Partial<Pick<VoiceTraceContext, "chatId" | "openaiCallId">>
) {
  if (!activeTraceContext) return
  activeTraceContext = { ...activeTraceContext, ...context }
  const chatId = context.chatId
  if (chatId == null) return

  pendingTraceEntries.forEach((entry) => {
    if (entry.chatId === null) entry.chatId = chatId
  })
  void flushPendingTraceEntries()
}

export function getActiveVoiceTraceContext() {
  return activeTraceContext
}

export function stopVoiceTraceContext() {
  if (activeTraceContext?.chatId === null) {
    pendingTraceEntries = []
  }
  activeTraceContext = null
}

export function traceEnabled() {
  return activeTraceContext !== null
}

// Drain the pending queue into IndexedDB. Flushes are serialized so
// overlapping calls can't double-write a batch; recording schedules one per
// entry and consecutive schedules coalesce into a single drain. Entries
// recorded before a chat owns the attempt stay pending until the chat id is
// known, so the chat-indexed export sees the whole attempt in order.
function flushPendingTraceEntries(): Promise<void> {
  const previousFlush = flushQueue
  flushQueue = (async () => {
    try {
      await previousFlush
      if (activeTraceContext?.chatId === null) {
        return
      }
      const batch = pendingTraceEntries
      if (batch.length === 0) {
        return
      }
      pendingTraceEntries = []

      for (const entry of batch) {
        await idbAppend<VoiceTraceEntry>(VOICE_TRACE_STORE, entry)
      }

      const count = await idbCount(VOICE_TRACE_STORE)
      if (count > MAX_VOICE_TRACE_ENTRIES) {
        await idbTrimOldest(VOICE_TRACE_STORE, count - MAX_VOICE_TRACE_ENTRIES)
      }
    } catch {
      // Trace persistence must never affect the voice session.
    }
  })()
  return flushQueue
}

export function setVoiceTraceEventSeq(seq: number | null) {
  activeTraceEventSeq = seq
}

export function currentTraceEventSeq() {
  return activeTraceEventSeq
}

export function voiceTracer(
  source: VoiceTraceSource,
  bound: Record<string, unknown> = {},
  boundEventSeq: number | null = activeTraceEventSeq
): VoiceTracer {
  const write = (
    level: VoiceTraceEntry["level"],
    summary: VoiceTraceEventName,
    detail?: Record<string, unknown>,
    direction?: "in" | "out"
  ) => {
    const mergedDetail = { ...bound, ...detail }
    const eventSeq = activeTraceEventSeq ?? boundEventSeq ?? undefined
    recordVoiceTrace({
      category: source,
      level,
      direction,
      summary,
      ...(eventSeq === undefined ? {} : { eventSeq }),
      detail: Object.keys(mergedDetail).length > 0 ? mergedDetail : undefined,
    })
  }

  return {
    info: (summary, detail, direction) =>
      write("info", summary, detail, direction),
    warn: (summary, detail, direction) =>
      write("warn", summary, detail, direction),
    error: (summary, detail, direction) =>
      write("error", summary, detail, direction),
    bind: (extra) =>
      voiceTracer(
        source,
        { ...bound, ...extra },
        activeTraceEventSeq ?? boundEventSeq
      ),
  }
}

interface LayoutShiftSourceLike {
  node?: Node | null
}

interface LayoutShiftEntryLike extends PerformanceEntry {
  value?: number
  hadRecentInput?: boolean
  sources?: LayoutShiftSourceLike[]
}

function isLayoutShiftEntry(
  entry: PerformanceEntry
): entry is LayoutShiftEntryLike {
  return (
    entry.entryType === "layout-shift" &&
    "value" in entry &&
    "hadRecentInput" in entry
  )
}

function timelineRowDetail(node: Node | null | undefined) {
  if (!(node instanceof Element)) return null
  const row = node.closest("[data-row-id]")
  if (!row) return null

  return {
    rowId: row.getAttribute("data-row-id"),
    rowKind: row.getAttribute("data-row-kind"),
  }
}

export function startVoiceLayoutShiftTrace(root: HTMLElement): () => void {
  if (!traceEnabled()) return () => undefined
  if (typeof PerformanceObserver === "undefined") return () => undefined

  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (!isLayoutShiftEntry(entry)) return

      const sourceRows = (entry.sources ?? [])
        .map((source) => timelineRowDetail(source.node))
        .filter((row): row is NonNullable<typeof row> => row !== null)

      recordVoiceTrace({
        category: "layout",
        level: "info",
        summary: "voice_timeline_layout_shift",
        detail: {
          value: entry.value ?? 0,
          hadRecentInput: entry.hadRecentInput === true,
          sourceRows,
          rootRowCount: root.querySelectorAll("[data-row-id]").length,
        },
      })
    })
  })

  try {
    observer.observe({ type: "layout-shift", buffered: true })
  } catch {
    observer.disconnect()
    return () => undefined
  }

  return () => observer.disconnect()
}

export function redactHeaders(headers: unknown): Record<string, unknown> {
  if (!isRecord(headers)) return {}

  const normalized = hasToJson(headers) ? headers.toJSON() : headers
  if (!isRecord(normalized)) return {}

  return Object.fromEntries(
    Object.entries(normalized).map(([key, value]) => [
      key,
      REDACTED_HEADERS.has(key.toLowerCase()) ? REDACTED : value,
    ])
  )
}

export function redactBody(body: unknown): unknown {
  if (Array.isArray(body)) {
    return body.map((entry) => redactBody(entry))
  }

  if (!isRecord(body)) return body

  return Object.fromEntries(
    Object.entries(body).map(([key, value]) => [
      key,
      REDACTED_BODY_FIELDS.has(key) ? REDACTED : redactBody(value),
    ])
  )
}

function nextTraceEntryId(ts: number) {
  traceEntrySequence += 1
  return `${ts}:${traceEntrySequence}`
}

function logDevTrace(entry: VoiceTraceEntry) {
  if ((entry.level === "warn" || entry.level === "error") && isDevelopment()) {
    const log = entry.level === "warn" ? console.warn : console.error
    log("[voice-trace]", entry.summary, entry.detail)
  }
}

export function recordVoiceTrace(
  entry: Omit<VoiceTraceEntry, "id" | "ts" | "voiceSessionId">
): void {
  const context = activeTraceContext
  if (!context) return

  recordVoiceTraceForContext(context, entry)
}

export function recordVoiceTraceForContext(
  context: VoiceTraceContext,
  entry: Omit<VoiceTraceEntry, "id" | "ts" | "voiceSessionId">
): void {
  const eventSeq = activeTraceEventSeq ?? entry.eventSeq
  const ts = nowMillis()
  const storedEntry: VoiceTraceEntry = {
    ...entry,
    ...(eventSeq === undefined ? {} : { eventSeq }),
    id: nextTraceEntryId(ts),
    chatId: context.chatId,
    voiceSessionId: context.voiceSessionId,
    openaiCallId: context.openaiCallId,
    ts,
  }
  logDevTrace(storedEntry)
  pendingTraceEntries.push(storedEntry)
  void flushPendingTraceEntries()
}

export async function readRetainedVoiceTraceSessions(
  chatId: number
): Promise<RetainedVoiceTraceSession[]> {
  await flushPendingTraceEntries()
  const [voiceEntries, openaiEntries] = await Promise.all([
    idbGetChatIndexBoundaryEntries<VoiceTraceEntry>(
      VOICE_TRACE_STORE,
      VOICE_TRACE_SESSION_INDEX,
      chatId
    ),
    idbGetChatIndexBoundaryEntries<VoiceTraceEntry>(
      VOICE_TRACE_STORE,
      VOICE_TRACE_LEGACY_SESSION_INDEX,
      chatId
    ),
  ])

  const scopedEntries = [
    ...voiceEntries.flatMap((entry) =>
      entry.voiceSessionId
        ? [
            {
              scope: {
                kind: "voice",
                id: entry.voiceSessionId,
              } satisfies VoiceTraceSessionScope,
              entry,
            },
          ]
        : []
    ),
    ...openaiEntries.flatMap((entry) =>
      entry.voiceSessionId === undefined && entry.openaiCallId
        ? [
            {
              scope: {
                kind: "legacy",
                id: entry.openaiCallId,
              } satisfies VoiceTraceSessionScope,
              entry,
            },
          ]
        : []
    ),
  ]
  const sessions = new Map<string, RetainedVoiceTraceSession>()
  scopedEntries.forEach(({ scope, entry }) => {
    const key = `${scope.kind}:${scope.id}`
    const session = sessions.get(key)
    if (session) {
      session.startedAt = Math.min(session.startedAt, entry.ts)
      session.latestAt = Math.max(session.latestAt, entry.ts)
      return
    }
    sessions.set(key, {
      scope,
      startedAt: entry.ts,
      latestAt: entry.ts,
    })
  })
  return Array.from(sessions.values()).sort(
    (left, right) => right.latestAt - left.latestAt
  )
}

async function readVoiceTraceChunk(
  chatId: number,
  scope: VoiceTraceScope,
  continuationKey?: number
): Promise<VoiceTraceExportChunk> {
  await flushPendingTraceEntries()
  let indexName = VOICE_TRACE_CHAT_INDEX
  let key: IDBValidKey = chatId
  if (scope.kind === "voice") {
    indexName = VOICE_TRACE_SESSION_INDEX
    key = [chatId, scope.id]
  } else if (scope.kind === "legacy") {
    indexName = VOICE_TRACE_LEGACY_SESSION_INDEX
    key = [chatId, scope.id]
  }

  const chunk = await idbGetNewestIndexChunk<VoiceTraceEntry>(
    VOICE_TRACE_STORE,
    indexName,
    key,
    continuationKey,
    VOICE_TRACE_EXPORT_CHUNK_SIZE
  )
  return {
    entries:
      scope.kind === "legacy"
        ? chunk.entries.filter((entry) => entry.voiceSessionId === undefined)
        : chunk.entries,
    continuationKey: chunk.continuationKey,
  }
}

export async function readVoiceTraceEntries(
  chatId: number,
  scope: VoiceTraceScope
): Promise<VoiceTraceEntry[]> {
  let chunk = await readVoiceTraceChunk(chatId, scope)
  const entries = [...chunk.entries]
  while (chunk.continuationKey !== undefined) {
    chunk = await readVoiceTraceChunk(chatId, scope, chunk.continuationKey)
    entries.push(...chunk.entries)
  }
  return entries
}

function isLightTraceEntry(entry: VoiceTraceEntry): boolean {
  if (LIGHT_TRACE_EXCLUDED_SOURCES.has(entry.category)) {
    return false
  }
  if (
    entry.summary === "realtime_event_applied" ||
    entry.summary.endsWith(".delta")
  ) {
    return false
  }
  if (entry.category === "http") {
    return (
      isRecord(entry.detail) &&
      typeof entry.detail.status === "number" &&
      entry.detail.status >= 400
    )
  }
  return (
    LIGHT_TRACE_SUMMARIES.has(entry.summary) ||
    LIGHT_TRACE_SUMMARY_PREFIXES.some((prefix) =>
      entry.summary.startsWith(prefix)
    )
  )
}

function lightTraceDetail(entry: VoiceTraceEntry): Record<string, unknown> {
  if (!isRecord(entry.detail)) {
    return {}
  }
  const detail: Record<string, unknown> = {}
  Object.entries(entry.detail).forEach(([key, value]) => {
    if (
      entry.summary === "error" &&
      key === "message" &&
      typeof value === "string"
    ) {
      detail[key] = value.slice(0, 200)
      return
    }
    const allowedValue =
      typeof value === "number" ||
      typeof value === "boolean" ||
      (LIGHT_TRACE_DETAIL_KEYS.has(key) &&
        (typeof value === "string" ||
          (Array.isArray(value) &&
            value.every(
              (item) => typeof item === "string" || typeof item === "number"
            ))))
    if (allowedValue) {
      detail[key] = value
    }
  })
  return detail
}

export function buildLightVoiceTraceEntries(
  entries: VoiceTraceEntry[]
): VoiceTraceEntry[] {
  return entries.filter(isLightTraceEntry).map((entry) => {
    const detail = lightTraceDetail(entry)
    return {
      id: entry.id,
      ts: entry.ts,
      chatId: entry.chatId,
      voiceSessionId: entry.voiceSessionId,
      openaiCallId: entry.openaiCallId,
      eventSeq: entry.eventSeq,
      category: entry.category,
      level: entry.level,
      direction: entry.direction,
      summary: entry.summary,
      detail: Object.keys(detail).length > 0 ? detail : undefined,
    }
  })
}

async function buildVoiceTraceFile(entries: VoiceTraceEntry[]): Promise<{
  file: File
  contentEncoding: "gzip" | "identity"
}> {
  const json = JSON.stringify(entries)
  if (typeof CompressionStream !== "function") {
    return {
      file: new File([json], "trace.json", { type: "application/json" }),
      contentEncoding: "identity",
    }
  }

  const gzipBlob = await new Response(
    new Blob([json]).stream().pipeThrough(new CompressionStream("gzip"))
  ).blob()
  return {
    file: new File([gzipBlob], "trace.json.gz", {
      type: "application/json",
    }),
    contentEncoding: "gzip",
  }
}

export async function buildLightVoiceTrace(
  chatId: number,
  voiceSessionId: string
): Promise<VoiceLightTraceExport> {
  const entries = await readVoiceTraceEntries(chatId, {
    kind: "voice",
    id: voiceSessionId,
  })
  const lightEntries = buildLightVoiceTraceEntries(entries)
  return {
    ...(await buildVoiceTraceFile(lightEntries)),
    entryCount: lightEntries.length,
    droppedCount: entries.length - lightEntries.length,
  }
}

export async function buildVoiceFeedbackTrace(
  chatId: number
): Promise<VoiceFeedbackTraceExport> {
  const entries = await readVoiceTraceEntries(chatId, { kind: "all" })
  const voiceSessionIds = uniq(
    entries.flatMap(({ voiceSessionId }) =>
      voiceSessionId ? [voiceSessionId] : []
    )
  )

  return {
    ...(await buildVoiceTraceFile(entries)),
    voiceSessionIds,
  }
}

export async function clearVoiceTrace(): Promise<void> {
  pendingTraceEntries = []
  await flushQueue
  await idbClearStore(VOICE_TRACE_STORE)
}

export function traceVoiceObjectSubscriptionRequested(args: {
  objectType: string
  objectId: string | number
  once: boolean
}) {
  if (args.objectType !== "voice_chat") return
  voiceTracer("socket").info("object_subscription_requested", {
    objectType: args.objectType,
    objectId: args.objectId,
    once: args.once,
  })
}

export function traceVoiceObjectSubscriptionQueued(args: {
  objectType: string
  objectId: string | number
}) {
  if (args.objectType !== "voice_chat") return
  voiceTracer("socket").info("object_subscription_queued_disconnected", {
    objectType: args.objectType,
    objectId: args.objectId,
  })
}

export function traceVoiceObjectSubscriptionUnsubscribed(args: {
  objectType: string
  objectId: string | number
  subscriptionId: string
}) {
  if (args.objectType !== "voice_chat") return
  voiceTracer("socket").info("object_subscription_unsubscribed", args)
}

export function traceVoiceObjectSubscriptionAuthorized(args: {
  objectType: string
  objectId: string | number
  subscriptionId: string
}) {
  if (args.objectType !== "voice_chat") return
  voiceTracer("socket").info("object_subscription_authorized", args)
}

export function traceVoiceObjectSubscriptionFailed(args: {
  objectType: string
  objectId: string | number
  message: string
}) {
  if (args.objectType !== "voice_chat") return
  voiceTracer("socket").warn("object_subscription_failed", args)
}

export function traceVoiceObjectSubscriptionEmit(args: {
  objectType: string
  objectId: string | number
  matcherCount: number
  oneShotMatcherCount: number
}) {
  if (args.objectType !== "voice_chat") return
  voiceTracer("socket").info(
    args.matcherCount > 0
      ? "object_subscription_emit_matched"
      : "object_subscription_emit_no_match",
    args
  )
}

export function traceVoiceChatObjectReceived(payload: SubscribableObjectEvent) {
  if (payload.object_type !== "voice_chat") return
  voiceTracer("socket").info("socket_voice_chat_object_received", {
    objectType: payload.object_type,
    objectId: payload.object_id,
    payloadType: payload.data.type,
  })
}
